import React, { useState } from 'react';
import './Blog.css';
import { getApiUrl } from '../config';

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState(window.__BLOGS_CACHE__ || []);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(!window.__BLOGS_CACHE__);
  const [loadingSlug, setLoadingSlug] = useState(null);

  React.useEffect(() => {
    fetch(getApiUrl('/blogs'))
      .then((res) => {
        if (!res.ok) throw new Error('API down');
        return res.json();
      })
      .then((data) => {
        setBlogPosts(data || []);
        window.__BLOGS_CACHE__ = data;
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching blogs:', err);
        if (!window.__BLOGS_CACHE__) {
          setBlogPosts([]);
        }
        setLoading(false);
      });
  }, []);

  const handleBlogClick = (e, slug) => {
    e.preventDefault();
    setLoadingSlug(slug);

    fetch(getApiUrl(`/blogs/${slug}`))
      .then((res) => {
        if (!res.ok) throw new Error('Failed to prefetch blog details');
        return res.json();
      })
      .then((data) => {
        if (!window.__BLOG_DETAIL_CACHE__) {
          window.__BLOG_DETAIL_CACHE__ = {};
        }
        window.__BLOG_DETAIL_CACHE__[slug] = data;
        setLoadingSlug(null);
        window.history.pushState({}, '', `/${slug}/`);
      })
      .catch((err) => {
        console.error('Error prefetching blog:', err);
        setLoadingSlug(null);
        // Fallback: navigate anyway so user is not stuck
        window.history.pushState({}, '', `/${slug}/`);
      });
  };




  const postsPerPage = 6;
  const totalPages = Math.ceil(blogPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <div className="blog-page">
      {/* Sub Hero Banner */}
      <section
        className="blog-hero"
        style={{
          backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/home-banner.jpg')`
        }}
      >
        <div className="blog-hero__container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1 className="blog-hero__title" style={{ marginBottom: '8px' }}>Blog</h1>
          <nav className="about-hero__breadcrumb" aria-label="breadcrumb">
            <a href="/" className="about-hero__breadcrumb-link">
              Home
            </a>
            <span className="about-hero__breadcrumb-separator">&gt;</span>
            <span className="about-hero__breadcrumb-current">Blog</span>
          </nav>
        </div>
      </section>

      {/* Blog Grid Section */}
      <section className="blog-grid-section">
        <div className="blog-grid__container">
          {loading ? (
            <div className="blog-grid">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="blog-card-skeleton">
                  <div className="skeleton-box blog-card-skeleton__img" />
                  <div className="blog-card-skeleton__content">
                    <div className="skeleton-box blog-card-skeleton__title" />
                    <div className="skeleton-box blog-card-skeleton__title-short" />
                    <div className="skeleton-box blog-card-skeleton__excerpt" />
                    <div className="skeleton-box blog-card-skeleton__excerpt-short" />
                    <div className="skeleton-box blog-card-skeleton__btn" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="blog-grid">
                {currentPosts.map((post) => (
                  <a key={post.id} href={`/${post.slug}/`} onClick={(e) => handleBlogClick(e, post.slug)} className="blog-card-link" style={{ textDecoration: 'none' }}>
                    <article className="blog-card">
                      <div className="blog-card__image-wrapper">
                        <img
                          src={post.image && post.image.startsWith('http') ? post.image : `${process.env.PUBLIC_URL}${post.image || '/assets/images/home-banner.jpg'}`}
                          alt={post.title}
                          className="blog-card__img"
                        />
                      </div>
                      <div className="blog-card__content">
                        {/* Meta date/author removed as requested */}
                        <h2 className="blog-card__title">
                          <span className="blog-card__title-link">
                            {post.title}
                          </span>
                        </h2>
                        <p className="blog-card__excerpt">{post.excerpt}</p>
                        <div className="blog-card__cta">
                          <span className="blog-card__btn">
                            {loadingSlug === post.slug ? (
                              <>
                                Loading...
                                <span className="blog-card-spinner" />
                              </>
                            ) : (
                              <>
                                Learn more
                                <svg className="blog-card__btn-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.22 5.03a.75.75 0 111.06-1.06l5.5 5.5a.75.75 0 010 1.06l-5.5 5.5a.75.75 0 11-1.06-1.06l4.168-4.17H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                                </svg>
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                    </article>
                  </a>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="blog-pagination">
                  <button
                    className="blog-pagination__btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    &lt; Prev
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`blog-pagination__num ${currentPage === i + 1 ? 'blog-pagination__num--active' : ''}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    className="blog-pagination__btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next &gt;
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;

